import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export interface ReportTemplate {
  header: {
    logo: string | null;
    logoPosition: "left" | "center" | "right";
    logoSize: "small" | "medium" | "large";
    reportTitle: string;
    useReportNameAuto: boolean;
    subtitle: string;
    titleAlign: "left" | "center" | "right";
    showCompanyInfo: boolean;
    companyName: string;
    addressLine1: string;
    addressLine2: string;
    showDivider: boolean;
    dividerColor: string;
    backgroundColor: string;
  };
  footer: {
    left: string;
    center: string;
    right: string;
    leftText: string;
    centerText: string;
    rightText: string;
    showDivider: boolean;
    fontSize: "small" | "normal";
    textColor: string;
  };
  colors: {
    primary: string;
    headerBg: string;
    headerText: string;
    altRow: string;
  };
  page: {
    size: "A4" | "Letter" | "A3";
    orientation: "portrait" | "landscape";
    margins: "normal" | "narrow" | "wide";
  };
  typography: {
    fontFamily: string;
    fontSize: number;
  };
}

export interface GeneralSettings {
  platformName: string;
  platformUrl: string;
  defaultCurrency: string;
  defaultDateFormat: string;
  defaultTimezone: string;
  defaultLanguage: string;
}

export interface BrandingSettings {
  logo: string | null;
  favicon: string | null;
  primaryColor: string;
}

export const DEFAULT_REPORT_TEMPLATE: ReportTemplate = {
  header: {
    logo: null,
    logoPosition: "left",
    logoSize: "medium",
    reportTitle: "{Report Name}",
    useReportNameAuto: true,
    subtitle: "",
    titleAlign: "left",
    showCompanyInfo: true,
    companyName: "PhazeX",
    addressLine1: "",
    addressLine2: "",
    showDivider: true,
    dividerColor: "#2563eb",
    backgroundColor: "#ffffff",
  },
  footer: {
    left: "date",
    center: "page_numbers",
    right: "confidential",
    leftText: "",
    centerText: "",
    rightText: "",
    showDivider: true,
    fontSize: "small",
    textColor: "#888888",
  },
  colors: {
    primary: "#2563eb",
    headerBg: "#2563eb",
    headerText: "#ffffff",
    altRow: "#f5f8ff",
  },
  page: {
    size: "A4",
    orientation: "landscape",
    margins: "normal",
  },
  typography: {
    fontFamily: "Helvetica",
    fontSize: 10,
  },
};

export const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  platformName: "PhazeX Hub",
  platformUrl: "hub.phazex.ai",
  defaultCurrency: "AED",
  defaultDateFormat: "DD/MM/YYYY",
  defaultTimezone: "Asia/Dubai",
  defaultLanguage: "English",
};

export const DEFAULT_BRANDING: BrandingSettings = {
  logo: null,
  favicon: null,
  primaryColor: "#2563eb",
};

type ConfigKey = "general_settings" | "report_template" | "branding";

export function useConfig<T>(key: ConfigKey, fallback: T) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: ["config", key, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ key: string; value: T | null }>(
        `/config/${key}`,
        { token: accessToken },
      );
      return (res.value ?? fallback) as T;
    },
  });
}

export function useSaveConfig(key: ConfigKey) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown) =>
      apiFetch<{ key: string; value: unknown }>(`/config/${key}`, {
        method: "PUT",
        body: { value },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["config", key] });
    },
  });
}
